/**
 * Hooks TanStack Query — connexions plateformes (S6 PR1).
 *
 * Pattern aligné sur `usePod.ts` (mêmes conventions de query keys + erreur
 * shape `{ detail }`).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { platformConnectionsApi } from '@/utils/api/platform-connections'
import type {
  EtsyOAuthStartResponse,
  PinterestBoardResponse,
  PinterestCancelBlockedResponse,
  PinterestMarkStandardResponse,
  PinterestOAuthStartResponse,
  PinterestPublishRequest,
  PinterestPublishResponse,
  PinterestQuotaState,
  Platform,
  PlatformConnectionResponse,
} from '@/types/platform-connections'

interface ApiError {
  detail: string
  status?: number
}

export const platformConnectionsKeys = {
  all: ['platform-connections'] as const,
  list: (platform?: Platform) =>
    [...platformConnectionsKeys.all, 'list', platform ?? null] as const,
  pinterestBoards: (connectionId: number) =>
    [...platformConnectionsKeys.all, 'pinterest-boards', connectionId] as const,
  pinterestQuota: () => [...platformConnectionsKeys.all, 'pinterest-quota'] as const,
}

export function usePlatformConnections(platform?: Platform) {
  return useQuery<PlatformConnectionResponse[]>({
    queryKey: platformConnectionsKeys.list(platform),
    queryFn: () => platformConnectionsApi.list(platform),
  })
}

export function useDisconnectPlatform() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, number>({
    mutationFn: platformConnectionsApi.disconnect,
    onSuccess: () => {
      // Toutes les listes (filtrées ou non) doivent refléter la suppression.
      void qc.invalidateQueries({ queryKey: platformConnectionsKeys.all })
    },
  })
}

/**
 * Mutation `startEtsyOAuth` — récupère l'URL d'autorisation Etsy puis la
 * retourne au caller. Le composant gère lui-même la navigation (redirect
 * `window.location.href = url` ou ouverture popup).
 *
 * On utilise une mutation plutôt qu'un useQuery pour éviter qu'un fetch
 * automatique au mount déclenche le flow OAuth — c'est strictement une
 * action utilisateur (clic sur "Connecter Etsy").
 */
export function useStartEtsyOAuth() {
  return useMutation<EtsyOAuthStartResponse, ApiError, void>({
    mutationFn: platformConnectionsApi.startEtsyOAuth,
  })
}

/**
 * Mutation `startPinterestOAuth` — pattern identique à Etsy. Pinterest
 * accepte le flow OAuth 2.0 standard (PAS de PKCE), state JWT signé porte
 * le `user_id` pour CSRF. Le composant `<PlatformConnectionCard>` consomme
 * `authorize_url` et fait `window.location.href = url` (full-page redirect).
 */
export function useStartPinterestOAuth() {
  return useMutation<PinterestOAuthStartResponse, ApiError, void>({
    mutationFn: platformConnectionsApi.startPinterestOAuth,
  })
}

/**
 * Lecture pure des boards Pinterest synchronisés pour une connexion.
 * Reste sur le cache TanStack Query (les boards ne changent pas tous les
 * jours côté Pinterest — la sync `refresh` invalide la query).
 *
 * `enabled` permet au composant de désactiver le fetch quand la connexion
 * n'est pas Pinterest (le BE retourne `[]` silencieusement de toutes
 * façons, mais autant ne pas tirer de requête).
 */
export function usePinterestBoards(connectionId: number | null, options?: { enabled?: boolean }) {
  return useQuery<PinterestBoardResponse[]>({
    queryKey: platformConnectionsKeys.pinterestBoards(connectionId ?? 0),
    queryFn: () => platformConnectionsApi.listPinterestBoards(connectionId!),
    enabled: connectionId !== null && (options?.enabled ?? true),
  })
}

/**
 * Re-sync les boards depuis Pinterest (bouton "Rafraîchir mes boards"
 * sur la card connectée). Sur succès, invalide la query lecture pour
 * forcer le re-fetch ; on retourne la liste à jour pour mise à jour
 * optimiste possible.
 */
export function useRefreshPinterestBoards() {
  const qc = useQueryClient()
  return useMutation<PinterestBoardResponse[], ApiError, number>({
    mutationFn: platformConnectionsApi.refreshPinterestBoards,
    onSuccess: (boards, connectionId) => {
      qc.setQueryData(platformConnectionsKeys.pinterestBoards(connectionId), boards)
    },
  })
}

/**
 * S7-PR2 — Publish Pinterest. La mutation accepte `{workId, payload}` et
 * pose un job dans la file (carousel atomique OU N pins separate selon
 * `pinterest_strategy`). La réponse peut être `blocked=true` (cascade
 * K=1+carousel — décision user requise) ou nominal (`queue_item_id`
 * dans la response).
 */
export function usePublishToPinterest() {
  return useMutation<
    PinterestPublishResponse,
    ApiError,
    { workId: number; payload: PinterestPublishRequest }
  >({
    mutationFn: ({ workId, payload }) => platformConnectionsApi.publishPinterest(workId, payload),
  })
}

/**
 * Action user "Publier en mono-image quand même" sur une row blocked
 * (K=1+carousel cascade). Crée un nouveau publish force_mono.
 */
export function useRetryPinterestBlockedMono() {
  return useMutation<PinterestPublishResponse, ApiError, { workId: number; resourceId: number }>({
    mutationFn: ({ workId, resourceId }) =>
      platformConnectionsApi.retryPinterestBlockedMono(workId, resourceId),
  })
}

/**
 * Action user "Annuler la publication" sur une row blocked. Marque la
 * row en `failed` avec `cancel_reason='user_canceled'` (forensique
 * préservée).
 */
export function useCancelPinterestBlocked() {
  return useMutation<
    PinterestCancelBlockedResponse,
    ApiError,
    { workId: number; resourceId: number }
  >({
    mutationFn: ({ workId, resourceId }) =>
      platformConnectionsApi.cancelPinterestBlocked(workId, resourceId),
  })
}

/**
 * Lecture pure du compteur quota Pinterest journalier (Settings UI badge
 * "Quota du jour"). Reset implicite à 00:00 UTC côté BE — le hook ne
 * polle pas, il rafraîchit à chaque montage de la page.
 */
export function usePinterestQuotaState(options?: { enabled?: boolean }) {
  return useQuery<PinterestQuotaState>({
    queryKey: platformConnectionsKeys.pinterestQuota(),
    queryFn: platformConnectionsApi.getPinterestQuotaState,
    enabled: options?.enabled ?? true,
  })
}

/**
 * Admin — flip `pinterest.is_standard=True` après accord Pinterest.
 * Idempotent côté BE.
 */
export function useMarkPinterestConnectionStandard() {
  const qc = useQueryClient()
  return useMutation<PinterestMarkStandardResponse, ApiError, number>({
    mutationFn: platformConnectionsApi.markPinterestConnectionStandard,
    onSuccess: () => {
      // La connection card affiche le bandeau Trial — on invalide la
      // liste connections pour le re-render.
      void qc.invalidateQueries({ queryKey: platformConnectionsKeys.all })
    },
  })
}
